import { toast } from 'react-toastify';

class AudioService {
  constructor() {
    this.supportsSpeechSynthesis = 'speechSynthesis' in window;
    this.voices = [];
    this.loadVoices();
  }

  loadVoices() {
    if (this.supportsSpeechSynthesis) {
      this.voices = speechSynthesis.getVoices();
      if (this.voices.length === 0) {
        // Wait for voices to load
        speechSynthesis.addEventListener('voiceschanged', () => {
          this.voices = speechSynthesis.getVoices();
        });
      }
    }
  }

  // Get available voices for a language
  getVoicesForLanguage(language) {
    const languageMap = {
      'English': ['en-US', 'en-GB', 'en'],
      'Hindi': ['hi-IN', 'hi'],
      'Marathi': ['mr-IN', 'mr'],
      'Gujarati': ['gu-IN', 'gu'],
      'Bengali': ['bn-IN', 'bn']
    };

    const langCodes = languageMap[language] || ['en'];
    return this.voices.filter(voice => 
      langCodes.some(code => voice.lang.startsWith(code))
    );
  }

  // Truncate text to fit 30-second audio clip
  truncateTextForAudio(text, maxDuration = 30) {
    // Approximate words per second for speech: 2.5-3 words per second
    // For 30 seconds: approximately 75-90 words
    const maxWords = Math.floor(maxDuration * 2.8); // Conservative estimate
    const words = text.trim().split(/\s+/);
    
    if (words.length <= maxWords) {
      return text;
    }
    
    const truncated = words.slice(0, maxWords).join(' ');
    return truncated + '...';
  }

  // Generate audio using Web Speech API
  async generateBrowserAudio(text, language = 'English', options = {}) {
    if (!this.supportsSpeechSynthesis) {
      throw new Error('Speech synthesis not supported in this browser');
    }

    return new Promise((resolve, reject) => {
      try {
        // Truncate text for 30-second audio
        const truncatedText = this.truncateTextForAudio(text, 30);
        
        toast.info('🎵 Generating 30-second audio clip...', { autoClose: 3000 });

        const utterance = new SpeechSynthesisUtterance(truncatedText);
        const availableVoices = this.getVoicesForLanguage(language);
        
        if (availableVoices.length > 0) {
          utterance.voice = availableVoices[0];
        }

        utterance.rate = options.rate || 0.9;
        utterance.pitch = options.pitch || 1.0;
        utterance.volume = options.volume || 1.0;

        // Create audio data URL placeholder
        const audioPlaceholder = this.createAudioPlaceholder(text, language);

        utterance.onend = () => {
          resolve({
            success: true,
            data: {
              originalTopic: text,
              generatedScript: truncatedText,
              audioBase64: audioPlaceholder.base64,
              downloadUrl: audioPlaceholder.dataUrl,
              filename: `audio_${language}_30sec_${Date.now()}.txt`,
              mimeType: 'text/plain',
              fileSize: audioPlaceholder.size,
              duration: Math.min(30, Math.ceil(truncatedText.length / 12)), // Max 30 seconds
              language: language,
              voice: utterance.voice?.name || 'Default',
              source: 'Browser TTS - 30sec',
              truncated: truncatedText.length < text.length
            }
          });
        };

        utterance.onerror = (error) => {
          reject(new Error(`Browser TTS failed: ${error.error}`));
        };

        speechSynthesis.speak(utterance);
      } catch (error) {
        reject(error);
      }
    });
  }

  // Try Free TTS API (ResponsiveVoice alternative)
  async generateFreeTTS(text, language = 'English') {
    try {
      // Truncate text for 30-second audio
      const truncatedText = this.truncateTextForAudio(text, 30);
      
      toast.info('🎵 Generating 30-sec free TTS...', { autoClose: 3000 });
      
      // Use a free TTS service like VoiceRSS (requires API key) or ResponsiveVoice
      const languageCode = this.getLanguageCode(language);
      
      // For now, return a text-based fallback since most free services require API keys
      const audioPlaceholder = this.createAudioPlaceholder(truncatedText, language);
      
      return {
        success: true,
        data: {
          originalTopic: text,
          generatedScript: truncatedText,
          audioBase64: audioPlaceholder.base64,
          downloadUrl: audioPlaceholder.dataUrl,
          filename: `audio_${language}_free_30sec_${Date.now()}.txt`,
          mimeType: 'text/plain',
          fileSize: audioPlaceholder.size,
          duration: Math.min(30, Math.ceil(truncatedText.length / 12)),
          language: language,
          voice: 'Free TTS - 30sec',
          source: 'Free TTS Service - 30sec',
          truncated: truncatedText.length < text.length
        }
      };
    } catch (error) {
      throw new Error(`Free TTS failed: ${error.message}`);
    }
  }

  // Get language code for TTS services
  getLanguageCode(language) {
    const codes = {
      'English': 'en-us',
      'Hindi': 'hi-in',
      'Marathi': 'mr-in',
      'Gujarati': 'gu-in',
      'Bengali': 'bn-in'
    };
    return codes[language] || 'en-us';
  }

  // Create audio placeholder for text content
  createAudioPlaceholder(text, language) {
    const placeholder = `🎵 Audio Content (${language})\n\nText: ${text}\n\nNote: This is a text representation of the audio content. In a production environment, this would be converted to actual audio using TTS services.\n\nGenerated at: ${new Date().toISOString()}`;
    const base64 = btoa(unescape(encodeURIComponent(placeholder)));
    
    return {
      base64: base64,
      dataUrl: `data:text/plain;charset=utf-8;base64,${base64}`,
      size: placeholder.length
    };
  }

  // Main method to generate audio with fallbacks
  async generateAudio(text, language = 'English', options = {}) {
    const methods = [
      // Primary: Browser TTS (reliable fallback)
      () => this.generateBrowserAudio(text, language, options),
      // Secondary: Free TTS services
      () => this.generateFreeTTS(text, language),
      // Final: Text fallback
      () => this.generateTextFallback(text, language)
    ];

    let lastError;
    
    for (const method of methods) {
      try {
        const result = await method();
        if (result.success) {
          return result;
        }
      } catch (error) {
        console.warn('Audio generation method failed:', error);
        lastError = error;
        continue;
      }
    }

    // This should never be reached due to final fallback, but just in case
    return this.generateTextFallback(text, language);
  }

  // Dedicated text fallback method
  generateTextFallback(text, language) {
    toast.warn('All audio methods failed, returning text content', { autoClose: 5000 });
    const audioPlaceholder = this.createAudioPlaceholder(text, language);
    
    return {
      success: true,
      data: {
        originalTopic: text,
        generatedScript: text,
        audioBase64: audioPlaceholder.base64,
        downloadUrl: audioPlaceholder.dataUrl,
        filename: `audio_${language}_fallback_tts_${Date.now()}.txt`,
        mimeType: 'text/plain',
        fileSize: audioPlaceholder.size,
        duration: Math.ceil(text.length / 10),
        language: language,
        voice: 'Text Fallback',
        source: 'Fallback Text Service'
      }
    };
  }

  // Generate multi-language audio with fallback method
  async generateMultiLanguageAudio(text, languages = ['English', 'Hindi', 'Marathi']) {
    // Generate individually using the main method
    try {
      toast.info('🌐 Generating multi-language audio with fallback method...', { autoClose: 3000 });

      const audioResults = [];
      for (const language of languages) {
        try {
          const result = await this.generateAudio(text, language);
          audioResults.push({
            language: language,
            ...result.data
          });
        } catch (langError) {
          console.warn(`Failed to generate audio for ${language}:`, langError);
          audioResults.push({
            language: language,
            error: langError.message,
            fallback: true
          });
        }
      }

      return {
        success: true,
        data: {
          originalText: text,
          languages: languages,
          audioFiles: audioResults,
          totalGenerated: audioResults.filter(r => !r.error).length,
          source: 'Multi-Service Audio Generation'
        }
      };
    } catch (error) {
      throw new Error(`Multi-language audio generation failed: ${error.message}`);
    }
  }

  // Generate actual audio file using Web Audio API (experimental)
  async generateWebAudioFile(text, language = 'English') {
    if (!this.supportsSpeechSynthesis) {
      throw new Error('Speech synthesis not supported');
    }

    try {
      // This is a more advanced approach that would require additional setup
      // For now, use the browser TTS method
      return await this.generateBrowserAudio(text, language);
    } catch (error) {
      throw new Error(`Web Audio generation failed: ${error.message}`);
    }
  }
}

export default AudioService;